import React, { useEffect, useMemo, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiExternalLink,
  FiMapPin,
  FiRefreshCw,
} from "react-icons/fi";
import { FaCity, FaStore, FaUsers } from "react-icons/fa";

import Reveal from "./ui/Reveal";

import { getAssociations } from "../services/associationsServices";
import { getAllCommerces } from "../services/commercesServices";
import { getAllServicesMunicipaux } from "../services/servicesMunicipauxServices";

import {
  ensureArray,
  normalizeAssociationPoint,
  normalizeCommercePoint,
  normalizeServicePoint,
  sortPoints,
} from "../pages/CarteInterets/utils/carteInteretsUtils";

import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  POINT_TYPES,
} from "../pages/CarteInterets/data/carteInteretsUiData";

const TYPE_ICONS = {
  association: FaUsers,
  commerce: FaStore,
  service: FaCity,
};

function ResizeHandler() {
  const map = useMap();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
    }, 120);

    return () => window.clearTimeout(timer);
  }, [map]);

  return null;
}

function MapBoundsController({ points = [], selectedPoint }) {
  const map = useMap();

  useEffect(() => {
    if (selectedPoint) {
      map.flyTo([selectedPoint.lat, selectedPoint.lng], 15, {
        duration: 0.6,
      });
      return;
    }

    if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((point) => [point.lat, point.lng]));
      map.fitBounds(bounds, {
        padding: [30, 30],
        maxZoom: 15,
      });
      return;
    }

    if (points.length === 1) {
      map.flyTo([points[0].lat, points[0].lng], 15, {
        duration: 0.6,
      });
      return;
    }

    map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
  }, [map, points, selectedPoint]);

  return null;
}

function TypeStatButton({ type, active, count, onClick }) {
  const meta = POINT_TYPES[type];
  const Icon = TYPE_ICONS[type] || FiMapPin;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group inline-flex min-h-11 shrink-0 items-center gap-2 rounded-2xl border px-4 text-sm font-bold shadow-sm transition hover:-translate-y-0.5 ${
        active
          ? "border-transparent text-white"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
      }`}
      style={active ? { backgroundColor: meta.color } : undefined}
    >
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-xl ${
          active ? "bg-white/20 text-white" : "bg-slate-100 dark:bg-slate-900"
        }`}
        style={!active ? { color: meta.color } : undefined}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>

      <span>{meta.shortLabel}</span>

      <span
        className={`rounded-full px-2 py-0.5 text-xs ${
          active
            ? "bg-white/20 text-white"
            : "bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-400"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function SelectedPointCard({ point }) {
  if (!point) {
    return (
      <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-white/80 p-5 text-center dark:border-slate-700 dark:bg-slate-950/70">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-3xl bg-[#1277B9]/10 text-[#1277B9]">
          <FiMapPin className="h-6 w-6" />
        </div>

        <p className="mt-4 text-base font-bold text-slate-950 dark:text-white">
          Aucun lieu sélectionné
        </p>

        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          Cliquez sur un point de la carte pour afficher un aperçu.
        </p>
      </div>
    );
  }

  const type = POINT_TYPES[point.type] || POINT_TYPES.service;

  return (
    <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="flex items-start gap-3">
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-sm"
          style={{ backgroundColor: type.color }}
        >
          {type.shortLabel.slice(0, 1)}
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
            {type.singular}
            {point.category ? ` · ${point.category}` : ""}
          </p>

          <h3 className="mt-1 line-clamp-2 text-xl font-bold leading-tight text-slate-950 dark:text-white">
            {point.title}
          </h3>
        </div>
      </div>

      {point.description && (
        <p className="mt-4 line-clamp-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
          {point.description}
        </p>
      )}

      {point.address && (
        <p className="mt-4 flex items-start gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
          <FiMapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#1277B9]" />
          <span className="line-clamp-2">{point.address}</span>
        </p>
      )}

      {point.mapsHref && (
        <a
          href={point.mapsHref}
          target="_blank"
          rel="noreferrer"
          className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d]"
        >
          Ouvrir dans Google Maps
          <FiExternalLink className="h-4 w-4" />
        </a>
      )}
    </div>
  );
}

function MiniMap({ points, selectedPoint, onSelectPoint }) {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <MapContainer
        center={
          selectedPoint
            ? [selectedPoint.lat, selectedPoint.lng]
            : DEFAULT_CENTER
        }
        zoom={DEFAULT_ZOOM}
        className="h-[420px] w-full"
        scrollWheelZoom={false}
      >
        <ResizeHandler />
        <MapBoundsController points={points} selectedPoint={selectedPoint} />

        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {points.map((point) => {
          const type = POINT_TYPES[point.type] || POINT_TYPES.service;
          const selected = selectedPoint?.id === point.id;

          return (
            <CircleMarker
              key={point.id}
              center={[point.lat, point.lng]}
              radius={selected ? 12 : 8}
              pathOptions={{
                color: type.stroke,
                fillColor: type.color,
                fillOpacity: selected ? 0.96 : 0.82,
                weight: selected ? 4 : 2,
              }}
              eventHandlers={{
                click: () => onSelectPoint(point),
              }}
            >
              <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
                <div className="max-w-[220px] text-xs">
                  <p className="font-bold">{point.title}</p>
                  {point.category && (
                    <p className="mt-0.5 text-[11px] opacity-80">
                      {point.category}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] font-semibold">
                    {type.singular}
                  </p>
                </div>
              </Tooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}

function MapSkeleton() {
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.75fr)]">
      <div className="h-[420px] animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
      <div className="h-[280px] animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

export default function CarteInteretsTeaser() {
  const [loading, setLoading] = useState(true);
  const [allPoints, setAllPoints] = useState([]);
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [activeTypes, setActiveTypes] = useState({
    association: true,
    commerce: true,
    service: true,
  });

  const loadPoints = async () => {
    setLoading(true);

    try {
      const [associations, commerces, services] = await Promise.all([
        getAssociations().catch(() => []),
        getAllCommerces().catch(() => []),
        getAllServicesMunicipaux().catch(() => []),
      ]);

      const associationPoints = ensureArray(associations)
        .filter((item) => item?.is_active !== false)
        .map(normalizeAssociationPoint)
        .filter(Boolean);

      const commercePoints = ensureArray(commerces)
        .filter((item) => item?.is_active !== false)
        .map(normalizeCommercePoint)
        .filter(Boolean);

      const servicePoints = ensureArray(services)
        .filter((item) => item?.is_active !== false && item?.is_public !== false)
        .map(normalizeServicePoint)
        .filter(Boolean);

      const rows = sortPoints([
        ...associationPoints,
        ...commercePoints,
        ...servicePoints,
      ]);

      setAllPoints(rows);
      setSelectedPointId(rows[0]?.id || null);
    } catch (error) {
      console.error("Erreur chargement teaser carte:", error);
      setAllPoints([]);
      setSelectedPointId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPoints();
  }, []);

  const stats = useMemo(() => {
    return {
      total: allPoints.length,
      association: allPoints.filter((point) => point.type === "association")
        .length,
      commerce: allPoints.filter((point) => point.type === "commerce").length,
      service: allPoints.filter((point) => point.type === "service").length,
    };
  }, [allPoints]);

  const visiblePoints = useMemo(() => {
    return allPoints.filter((point) => activeTypes[point.type]);
  }, [allPoints, activeTypes]);

  const selectedPoint = useMemo(() => {
    return (
      visiblePoints.find((point) => point.id === selectedPointId) ||
      visiblePoints[0] ||
      null
    );
  }, [visiblePoints, selectedPointId]);

  useEffect(() => {
    if (!visiblePoints.length) {
      setSelectedPointId(null);
      return;
    }

    setSelectedPointId((current) => {
      const stillVisible = visiblePoints.some((point) => point.id === current);
      return stillVisible ? current : visiblePoints[0].id;
    });
  }, [visiblePoints]);

  const toggleType = (type) => {
    setActiveTypes((previous) => ({
      ...previous,
      [type]: !previous[type],
    }));
  };

  if (!loading && !allPoints.length) return null;

  return (
    <section
      id="carte-interets-teaser"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
      aria-labelledby="carte-teaser-title"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-emerald-300/15 blur-3xl dark:bg-emerald-500/10" />
        <div className="absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-sky-300/10 blur-3xl dark:bg-sky-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                Carte interactive
              </div>

              <h2
                id="carte-teaser-title"
                className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
              >
                Une carte pour tout retrouver
              </h2>

              <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600 dark:text-slate-300">
                Visualisez rapidement les associations, commerces, artisans et
                services municipaux de la commune sur une carte simplifiée.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <Link
              to="/carte-interets"
              className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-[#1277B9]/40 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:hover:border-sky-500/40 dark:hover:text-sky-300"
            >
              Voir la carte complète
              <FiArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <Reveal>
          <div className="mb-5 flex flex-col gap-4 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:flex-row sm:items-center sm:justify-between">
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0">
              <TypeStatButton
                type="association"
                active={activeTypes.association}
                count={stats.association}
                onClick={() => toggleType("association")}
              />
              <TypeStatButton
                type="commerce"
                active={activeTypes.commerce}
                count={stats.commerce}
                onClick={() => toggleType("commerce")}
              />
              <TypeStatButton
                type="service"
                active={activeTypes.service}
                count={stats.service}
                onClick={() => toggleType("service")}
              />
            </div>

            <button
              type="button"
              onClick={loadPoints}
              disabled={loading}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
            >
              <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
          </div>
        </Reveal>

        {loading ? (
          <MapSkeleton />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.75fr)] lg:items-start">
            <Reveal>
              <MiniMap
                points={visiblePoints}
                selectedPoint={selectedPoint}
                onSelectPoint={(point) => setSelectedPointId(point?.id || null)}
              />
            </Reveal>

            <Reveal>
              <div className="space-y-5">
                <SelectedPointCard point={selectedPoint} />

                <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Lieux référencés
                  </p>

                  <p className="mt-2 text-3xl font-bold text-slate-950 dark:text-white">
                    {visiblePoints.length}
                  </p>

                  <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                    lieu{visiblePoints.length > 1 ? "x" : ""} visible
                    {stats.total ? ` sur ${stats.total}` : ""}
                  </p>

                  <Link
                    to="/carte-interets"
                    className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900"
                  >
                    Rechercher un lieu
                    <FiArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}